// The caller's own recent receipts, as a pick list. Used by Mark bought and by
// the Receipts card's Link a receipt; both mount it only while their popover is
// open, so it loads fresh each time.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import Checkbox from '../components/ui/Checkbox';
import Tooltip from '../components/ui/Tooltip';
import StatusPill from '../Receipts/StatusPill';
import { formatEventDate } from '../utils/date-utils';
import { errorMessage, formatCents, useRequisitionsApi, type ReceiptCandidate } from './requisitionsApi';
import Reveal from '../components/ui/Reveal';

export const MAX_RECEIPTS = 10;

interface Props {
  requisitionId: string;
  /** Receipts already linked: not offered again. */
  excludePids?: string[];
  selected: string[];
  onChange: (pids: string[]) => void;
}

const ReceiptCandidates: React.FC<Props> = ({ requisitionId, excludePids = [], selected, onChange }) => {
  const reqApi = useRequisitionsApi();
  const [rows, setRows] = useState<ReceiptCandidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    setRows(null);
    try {
      setRows(await reqApi.receiptCandidates(requisitionId));
    } catch (err) {
      setError(errorMessage(err, 'Could not load your receipts.'));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requisitionId]);

  if (error) {
    return (
      <Alert variant="note" onRetry={load} retryLabel="Try again" retryTip="Load your receipts again" className="!flex w-full">
        {error}
      </Alert>
    );
  }

  if (rows === null) {
    return (
      <SkeletonGroup label="Loading your receipts" className="space-y-1.5">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-11 rounded-md" />
        ))}
      </SkeletonGroup>
    );
  }

  const offered = rows.filter((r) => !excludePids.includes(r.pid));

  if (!offered.length) {
    return (
      <EmptyState variant="area" className="!px-0 !py-3">
        <p className="m-0">No receipts of yours from the last 45 days to link.</p>
        <p className="m-0 mt-0.5 text-on-surface-variant">
          Upload the receipt first, then come back and link it.{' '}
          <Link to="/receipts" className="font-medium text-primary hover:underline">
            Go to receipts
          </Link>
        </p>
      </EmptyState>
    );
  }

  const full = selected.length >= MAX_RECEIPTS;
  const toggle = (pid: string) =>
    onChange(selected.includes(pid) ? selected.filter((p) => p !== pid) : [...selected, pid]);

  return (
    <fieldset className="min-w-0">
      <legend className="sr-only">Your receipts from the last 45 days</legend>
      <ul className="max-h-64 space-y-1 overflow-y-auto pr-1">
        {offered.map((r) => {
          const checked = selected.includes(r.pid);
          return (
            <li key={r.pid}>
              <Checkbox
                fill
                checked={checked}
                disabled={!checked && full}
                onChange={() => toggle(r.pid)}
                className={`rounded-md px-2.5 py-2 !gap-3 ${checked ? 'bg-primary/15' : 'hover:bg-surface-container-highest'} ${!checked && full ? 'opacity-50' : ''}`}
                label={
                  <span className="flex items-center gap-3">
                    <span className="min-w-0 flex-1">
                      <Tooltip label={r.vendor || ''} truncated>
                        <span className="block truncate text-sm text-on-surface">
                          {r.vendor || 'Vendor not read yet'}
                        </span>
                      </Tooltip>
                      <span className="block text-xs tabular-nums text-on-surface-variant">
                        {r.purchase_date ? formatEventDate(r.purchase_date, 'MMM D') : 'No date'}
                      </span>
                    </span>
                    <span className="text-sm tabular-nums text-on-surface">{formatCents(r.total_cents)}</span>
                    <StatusPill status={r.status} />
                  </span>
                }
              />
            </li>
          );
        })}
      </ul>
      <Reveal show={!!full}>
        <p className="mt-1.5 text-xs text-on-surface-variant">Ten is the most one request takes at once.</p>
      </Reveal>
    </fieldset>
  );
};

export default ReceiptCandidates;
